import React, { useContext, useEffect, useState } from 'react';
import {
  CheckboxField,
  RadioGroupField
} from '@trp-beacon-ui-kit/react-controls';
import cn from 'classnames';
import { FlowContext } from '../../core/flow-engine';
import SectionHeading from '../Typography/SectionHeading/SectionHeading';
import EditButton from '../Typography/EditButton/EditButton';
import { RenderRPSOCRExistingAgreements } from '../RPSOCRChooseMyFunds/RPSOCRChooseMyFunds';
import {
  toggleExistingAgreements,
  shouldValidateExistingAgreementsSection,
  checkDuplicateFunds,
  getOCRExistingAgreementTitle,
  getOCRExistingAgreeValue,
  getOCRExistingAgreeRolloverValue,
  filterExistingAgreementsType
} from './utils';
import { accountTypes } from '../../flows/shared/utils';

import styles from './RPSOCRExistingFunds.css';

const RenderRPSOCRAgreementsByType = ({
  filteredExistingAgreeArr,
  selectedExistingFunds,
  setSelectedExistingFunds
}) => {
  return (
    <div>
      <div className="existing-funds">
        <table>
          <thead>
            <tr className="table-header-row">
              <th className="fund-name-th">Fund Name</th>
            </tr>
          </thead>
          <tbody className="funds-table-body">
            {filteredExistingAgreeArr?.map(fund => {
              const {
                agreementKey: { agreementNumber },
                eligibleForTransaction,
                ticker
              } = fund;

              const defaultSelectedFund =
                selectedExistingFunds?.length > 0 &&
                selectedExistingFunds?.some(selectedFund => {
                  return (
                    selectedFund?.agreementKey?.agreementNumber ===
                    agreementNumber
                  );
                });

              const disableDuplicateFund =
                selectedExistingFunds?.length > 0 &&
                checkDuplicateFunds(
                  agreementNumber,
                  ticker,
                  filteredExistingAgreeArr,
                  selectedExistingFunds
                );

              const inEligibleFund = !eligibleForTransaction;
              const disableFund = disableDuplicateFund || inEligibleFund;

              return (
                <tr
                  key={`${fund.ticker}-${fund?.agreementKey?.uniqueKey}`}
                  className={cn('fund-row', {
                    disabled: disableFund
                  })}>
                  <td className="fund-type-cell">
                    <CheckboxField
                      name={`checkbox ${fund.ticker}`}
                      title={`${fund.fundName} (${fund.ticker}) - ${fund?.agreementKey?.agreementNumber}`}
                      checked={defaultSelectedFund}
                      onClick={() => {
                        toggleExistingAgreements(
                          fund,
                          agreementNumber,
                          selectedExistingFunds,
                          setSelectedExistingFunds
                        );
                      }}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <style jsx>{styles}</style>
    </div>
  );
};

/* eslint-disable complexity */
const RPSOCRExistingFunds = ({
  existingAgreements,
  next,
  selectedExistingFunds,
  setSelectedExistingFunds,
  editExistingAgreements,
  setEditExistingAgreements,
  existingAgreementsValid,
  updatedExistingAgreements,
  existingAgreementType,
  accountType
}) => {
  const {
    formProps,
    appState: {
      store: {
        loadedData: { existingAgreementsEditMode }
      }
    }
  } = useContext(FlowContext);

  useEffect(() => {
    setEditExistingAgreements(existingAgreementsEditMode);
  }, [existingAgreementsEditMode]);

  const isValid = shouldValidateExistingAgreementsSection(
    editExistingAgreements,
    existingAgreementsValid
  );

  const [selectedExistingAgreeType, setSelectedExistingAgreeType] = useState(
    existingAgreementType
  );

  const handleClick = async () => {
    if (editExistingAgreements) {
      await next({
        section: 'updateExistingAgreements',
        updatedExistingAgreements: selectedExistingFunds,
        existingAgreementType: selectedExistingAgreeType
      })();
    } else {
      await next('unlockRPSExistingFundsSection')();
    }
  };

  const filteredExistingAgreeArr = filterExistingAgreementsType(
    existingAgreements,
    accountType
  );

  const handleExistingAgreeType = event => {
    // clear selected existing funds array when agreement type changes
    setSelectedExistingFunds([]);
    // set existing agreement type based on selection
    if (event.target.value === accountTypes.TRADITIONAL) {
      setSelectedExistingAgreeType(accountTypes.TRADITIONAL);
    } else if (event.target.value === accountTypes.ROTH) {
      setSelectedExistingAgreeType(accountTypes.ROTH);
    } else if (event.target.value === accountTypes.ROTH_ROLLOVER) {
      setSelectedExistingAgreeType(accountTypes.ROTH_ROLLOVER);
    } else {
      setSelectedExistingAgreeType(accountTypes.ROLLOVER);
    }
  };

  const radioOptions = [
    {
      title: `${getOCRExistingAgreementTitle(accountType)}IRA`,
      value: `${getOCRExistingAgreeValue(accountType)}`,
      defaultChecked:
        existingAgreementType === accountTypes.TRADITIONAL ||
        existingAgreementType === accountTypes.ROTH,
      disabled: filteredExistingAgreeArr?.[0].length === 0
    },
    {
      title: `${getOCRExistingAgreementTitle(accountType)}Rollover IRA`,
      value: `${getOCRExistingAgreeRolloverValue(accountType)}`,
      defaultChecked:
        existingAgreementType === accountTypes.ROLLOVER ||
        existingAgreementType === accountTypes.ROTH_ROLLOVER,
      disabled: filteredExistingAgreeArr?.[1].length === 0
    }
  ];

  return (
    <>
      <div className="section-header">
        <SectionHeading text="Existing IRA Accounts" />
      </div>
      <div className="section-content">
        {editExistingAgreements && (
          <>
            <div className="existing-agreement-option">
              <RadioGroupField
                title="Options"
                options={radioOptions}
                horizontal
                onChange={handleExistingAgreeType}
                {...formProps.existingAgreementType}
                name="existing-agreements"
              />
            </div>
            {(selectedExistingAgreeType === accountTypes.TRADITIONAL ||
              selectedExistingAgreeType === accountTypes.ROTH) && (
              <RenderRPSOCRAgreementsByType
                filteredExistingAgreeArr={filteredExistingAgreeArr?.[0]}
                selectedExistingFunds={selectedExistingFunds}
                setSelectedExistingFunds={setSelectedExistingFunds}
              />
            )}
            {(selectedExistingAgreeType === accountTypes.ROLLOVER ||
              selectedExistingAgreeType === accountTypes.ROTH_ROLLOVER) && (
              <RenderRPSOCRAgreementsByType
                filteredExistingAgreeArr={filteredExistingAgreeArr?.[1]}
                selectedExistingFunds={selectedExistingFunds}
                setSelectedExistingFunds={setSelectedExistingFunds}
              />
            )}
          </>
        )}
        {!editExistingAgreements && (
          <div>
            {updatedExistingAgreements?.length > 0 ? (
              <RenderRPSOCRExistingAgreements
                updatedExistingAgreements={updatedExistingAgreements}
              />
            ) : (
              <div className="no-funds">No existing agreements selected.</div>
            )}
          </div>
        )}
        <EditButton
          onClick={handleClick}
          currentlyEditing={editExistingAgreements}
          disabled={!isValid}
        />
        <style jsx>{styles}</style>
      </div>
    </>
  );
};

/* eslint-enable complexity */
export default RPSOCRExistingFunds;
